import type {
  AdSizeSlotPayload,
  CreativeGamIdentifiers,
  SizeInfoEvent,
} from "common-types";
import type { AdNodeManager } from "../../../core/AdNodeManager";
import type { SlotName } from "../../../config/platforms/types";

import { pushTrack } from "utils";
import { parseSizes } from "../helpers/parseAdSlotSizes";
import {
  sizeToString,
  getAllConfiguredSlotSizes,
} from "../helpers/getSlotSizes";

export function sendSizeInfoTrack(
  track: SizeInfoEvent,
  slot: googletag.Slot,
  nodeManager: AdNodeManager,
  eventSize: googletag.events.SlotRenderEndedEvent["size"] | undefined,
): void {
  const slotIdName = slot.getSlotElementId() as SlotName;
  const node = nodeManager.getAdNodeByName(slotIdName);
  if (!node) {
    return;
  }

  const payload: AdSizeSlotPayload = {
    slotName: slotIdName,
    slotInfo: {
      ...getSlotResponseInfo(slot),
      selectedSize: getSelectedSize(eventSize),
      configuredSizes: getAllConfiguredSlotSizes(node),
      parsedSizes: parseSizes(slot),
    },
  };
  pushTrack(track, payload);
}

export function getSelectedSize(
  eventSize: googletag.events.SlotRenderEndedEvent["size"] | undefined,
): string | undefined | null {
  if (eventSize === undefined) {
    // This means it was not provided to the function; this should occur when the data is not applicable
    // for example when sending the `SLOT_REQUESTED` event and a size has not been selected yet
    // TODO: change this to a value for not applicable??
    return undefined;
  } else if (eventSize === null) {
    // null value is for empty ad slots: https://developers.google.com/publisher-tag/reference#googletag.events.SlotRenderEndedEvent.size
    return null;
  }
  return sizeToString(eventSize);
}

export function getSlotResponseInfo(
  slot: googletag.Slot,
): CreativeGamIdentifiers {
  const slotResponseInfo = slot.getResponseInformation?.();
  if (!slotResponseInfo) {
    return {
      advertiserId: undefined,
      creativeId: undefined,
      lineItemId: undefined,
      orderId: undefined,
    };
  }

  return {
    advertiserId: slotResponseInfo.advertiserId,
    creativeId: slotResponseInfo.creativeId,
    lineItemId: slotResponseInfo.lineItemId,
    orderId: slotResponseInfo.campaignId,
  };
}
